/**
 * A→B flip adapter (design rev A.1, JOINT-countersigned at 53c3c73) — Pipeline B's
 * sphere-facing hook.
 *
 * Wraps the UNMODIFIED useVoiceDaemon() and exposes the exact PTTState surface
 * AppShell consumes from usePTT, so the sphere's call surface swaps with a two-line
 * AppShell diff. Interface-matching ONLY — no fallback, no retry, no routing.
 *
 * Type discipline (rev A.1): the ONLY reference to Pipeline A's hook module is the
 * `import type` below — compile-erased. NO runtime import of usePTT.tsx (or any A
 * module) exists here; runtime imports are useVoiceDaemon, lib/voiceBinding, React.
 *
 * Device-side revision: the browser now owns capture/playback edges, so the mic
 * meter is real device input. `agentAmplitude` still replicates usePTT's own
 * synthetic 3 Hz oscillator (same constants, same agent_speaking gate) — presentation
 * parity, not invention.
 *
 * Error-tap semantics (design §2a, Ghost-accepted): the daemon hook holds `error`
 * state with no A-style timed bounce, so a tap from `error` starts FRESH (one-tap
 * retry) instead of requiring an end()+start() double tap.
 *
 * Fallback (design §4): NOT BUILT. A B failure surfaces as `error` — there is no
 * silent Pipeline A fallback. That mechanism is a separate, later piece.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MicMeter, PTTState, UsePTTOptions } from './usePTT';
import { useVoiceDaemon } from './useVoiceDaemon';
import { resolveVoiceBinding } from '../lib/voiceBinding';
import { assertCallAllowed } from '../lib/callTarget';

// Same synthetic-presentation constants as usePTT's agent oscillator.
const OSC_FREQ_HZ = 3;
const OSC_BASELINE = 0.35;
const OSC_AMPLITUDE = 0.15;

export function useVoiceDaemonPTT({ activeDomain, activeAgent }: UsePTTOptions): PTTState {
  const daemon = useVoiceDaemon();
  const [error, setError] = useState<string | null>(null);
  const [agentAmplitude, setAgentAmplitude] = useState(0);

  const stateRef = useRef(daemon.state);
  useEffect(() => { stateRef.current = daemon.state; }, [daemon.state]);
  // Read through a ref for the same reason as state: the tap handler is memoised
  // and must see the CURRENT call, not the one that existed when it was built.
  const boxCallRef = useRef(daemon.boxCall);
  useEffect(() => { boxCallRef.current = daemon.boxCall; }, [daemon.boxCall]);

  // Agent oscillator — identical synthesis to usePTT (3 Hz sin envelope over a
  // baseline), gated on agent_speaking; stops itself the moment the state leaves.
  const oscRafRef = useRef<number | null>(null);
  useEffect(() => {
    if (daemon.state !== 'agent_speaking' || oscRafRef.current != null) return;
    const t0 = Date.now();
    const tick = () => {
      if (stateRef.current !== 'agent_speaking') {
        setAgentAmplitude(0);
        oscRafRef.current = null;
        return;
      }
      const t = (Date.now() - t0) / 1000;
      const env = Math.abs(Math.sin(t * Math.PI * 2 * OSC_FREQ_HZ));
      setAgentAmplitude(OSC_BASELINE + OSC_AMPLITUDE * env);
      oscRafRef.current = requestAnimationFrame(tick);
    };
    oscRafRef.current = requestAnimationFrame(tick);
  }, [daemon.state]);
  useEffect(() => () => {
    if (oscRafRef.current != null) cancelAnimationFrame(oscRafRef.current);
  }, []);

  const { start, end } = daemon;

  /**
   * SEAM 1 — TAP TO START, TAP TO END. NO THIRD STATE (owner ruling 2026-07-30).
   *
   * THE SPHERE IS A CALL BUTTON. It has exactly two states: there is no call, or there
   * is. A tap when there is no call starts one. A tap at ANY other moment ends it.
   *
   * `error` is not an exception, and that is the whole point of this revision. The
   * previous cut started fresh from `error` — a documented one-tap retry, written when
   * an error state implied a dead session. DEVICE_PLAYBACK_BLOCKED broke that premise:
   * on 2026-07-30 a healthy call (first frame forwarded 1s later) sat in `error` because
   * the iOS pump element rejected, the owner tapped, and start() opened a SECOND session
   * on top of the live one. Two windows then tore each other's peer down for as long as
   * they both lived. Seam 2 removes the false error; this removes the third state that
   * turned it into a second call.
   *
   * `connecting` needs no special case either — ending while connecting IS the cancel,
   * and end() runs the same teardown, releasing the microphone and rejecting anything
   * parked on the start path.
   */
  const ptt = useCallback(() => {
    // ── THE GATE READS THE BOX, NOT THE STATE (owner ruling 2026-08-05) ──────
    // `boxCall != null` means the next tap ENDS. There is no other reading.
    //
    // The gate was `state === 'idle' ? start : end`, which was correct only while
    // `state` decided whether a call existed. It no longer does — boxCall does —
    // and the two disagree exactly when it matters: an undelivered end leaves this
    // window at 'idle' while the box still holds the call, so the next tap would
    // have STARTED A SECOND CALL on top of the live one. That is the 2026-07-30
    // two-window fight, reachable again through a different door.
    if (boxCallRef.current != null) {
      setError(null);
      end();
      return;
    }
    if (stateRef.current === 'idle') {
      setError(null);
      // Atomic, fail-closed {domain, agent} resolution — never a substituted default
      // agent (project_domain_agent_pair_drift; same contract as usePTT).
      const binding = resolveVoiceBinding({ domain: activeDomain, agent: activeAgent });
      if (!binding.ok) {
        setError(
          binding.reason === 'no_agent'
            ? 'Select an agent before starting voice'
            : 'No domain selected for voice',
        );
        return;
      }
      void start(binding.domain, binding.agent).catch((e) => {
        // B failure surfaces as error — no silent A fallback (design §4).
        setError(String((e as Error)?.message ?? e));
      });
      return;
    }
    // No box call, but this window is mid-something — connecting, or a start that
    // has not yet produced one. END is still the honest meaning: ending while
    // connecting IS the cancel.
    setError(null);
    end();
  }, [activeDomain, activeAgent, start, end]);

  // The wire guard, bound to the daemon's own start. `boxCallRef` is the CURRENT
  // call — the same ref the tap handler reads for the same reason.
  const guardedStart = useCallback(
    async (domain: string, agent: string) => {
      assertCallAllowed(boxCallRef.current, domain, agent);
      return start(domain, agent);
    },
    [start],
  );

  const { muted, setMuted } = daemon;
  const toggleMute = useCallback(() => setMuted(!muted), [muted, setMuted]);

  return useMemo(
    () => ({
      state: daemon.state,
      // Passed through unchanged: DOES THE BOX HOLD A CALL. The surface's whole
      // notion of "in a call" hangs off this and never off state.
      boxCall: daemon.boxCall,
      // Passed through unchanged: this call was already running when the window
      // reached it. The surface must be able to say so — a call nobody can tell
      // apart from one they just started is a call nobody ends.
      adopted: daemon.adopted,
      // Passed through unchanged: the peer is up, as distinct from the box
      // having accepted the session. CallKit's connectedAt reads this.
      mediaConnected: daemon.mediaConnected,
      userAmplitude: daemon.userAmplitude,
      agentAmplitude,
      partialTranscript: daemon.partialTranscript,
      agentText: daemon.agentText,
      ptt,
      // ── THE PER-AGENT CALL SURFACE (owner ruling 2026-08-27) ─────────────
      // Passed through so a per-agent button can call THIS agent without
      // constructing its own daemon. AppShell.tsx:79 owns the ONE client; a
      // second one would bring its own call-id contract, which is precisely
      // what the isolation rule exists to prevent.
      // GUARDED AT THE WIRE. Every caller of the published start passes this,
      // not only the button that renders a refusal. Reads the CURRENT box call
      // through the ref, so it cannot be stale in the way a render can.
      start: guardedStart,
      // The daemon's own binding intent. NOT the authority on who a call is
      // with — `boxCall` is, and its domain/agent are the pair the box
      // ACTUALLY has rather than the one requested. Nothing should read this
      // to answer "who am I talking to"; it is here because the context
      // carries the daemon's surface, not because it settles anything.
      daemonBinding: daemon.activeAgent,
      error: error || daemon.error,
      muted,
      // Passed through unchanged: how long the box says this mute has stood. A
      // surface that shows a changed icon and nothing else is what let a call sit
      // muted for ten minutes on 2026-08-29 with the owner talking into it.
      mutedStandingS: daemon.mutedStandingS,
      toggleMute,
      meter: daemon.meter as MicMeter,
    }),
    [daemon.state, daemon.boxCall, daemon.adopted, daemon.mediaConnected, daemon.userAmplitude, daemon.meter, agentAmplitude,
     daemon.partialTranscript, daemon.agentText, daemon.error, ptt, error, muted,
     daemon.mutedStandingS, toggleMute,
     guardedStart, daemon.activeAgent],
  );
}
